import { AttachFileOutlined, CloseRounded } from "@mui/icons-material";
import { Box, Button } from "@mui/material";
import { AgGridReact } from "ag-grid-react";
import { isEmpty, isNil, size } from "lodash";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import SelectFileAssignmentInput from "../../_src_shared/components/inputs/SelectFileAssignmentInput";
import { columnTypesDate } from "../../_src_shared/utils/agGrid/columnTypes/columnTypesDate";
import AgGridTableWrapper from "../agGrid/AgGridTableWrapper";
import FileUploadField from "../inputs/FileUploadField";
import InputWrapper from "../inputs/shared/InputWrapper";
import Flx from "../layout/Flx";

const FileUploadProcess = ({
  initialFiles,
  docType,
  docGroup,
  keyName,
  sx,
  loading,
  background,
  onSubmit,
  borderSize = 1.5,
  categoryAndTypeLabel,
  disableCategoryAndTypeSelection = false,
  disableCategoryAndTypeSelectionCount = false,
  children,
}) => {
  const [files, setFiles] = useState(initialFiles);
  const [assignedDocGroup, setAssignedDocGroup] = useState(docGroup || null);
  const [assignedDocType, setAssignedDocType] = useState(docType || null);
  const docGroupTypeLabel = useMemo(() => {
    if (categoryAndTypeLabel) {
      return categoryAndTypeLabel;
    }

    return "Document Category";
  }, [categoryAndTypeLabel]);

  const updateFilesMetadata = (files, docGroup, docType) => {
    // Guard clause for empty files array
    if (!files || files.length === 0) {
      return [];
    }

    // Create a new array with updated metadata
    return files.map((file) => {
      // Create a copy of the file to avoid mutating the original
      const updatedFile = { ...file };

      // Update docGroup if provided
      if (docGroup !== undefined && docGroup !== null) {
        updatedFile.docGroup = docGroup;

        // Clear docType if docGroup changes and the types don't match
        // This assumes docType is dependent on docGroup
        if (updatedFile.docType && docType === undefined) {
          updatedFile.docType = undefined;
        }
      }

      // Update docType if provided
      if (docType !== undefined && docType !== null) {
        updatedFile.docType = docType;
      }

      // Clear both if both are nil
      if (isNil(docType) && isNil(docGroup)) {
        updatedFile.docGroup = undefined;
        updatedFile.docType = undefined;
      }

      return updatedFile;
    });
  };

  const onDocGroupTypeChange = useCallback(
    (dg, dt) => {
      // console.log({ dg, dt });
      let updatedFiles = updateFilesMetadata(files, dg, dt);
      setAssignedDocGroup(dg);
      setAssignedDocType(dt);
      setFiles(updatedFiles);
    },
    [files],
  );

  const onFilesAdded = useCallback(
    (newFiles) => {
      // console.log("newFiles", newFiles);
      // Use the assigned values from state, not the callback params
      let updatedFiles = updateFilesMetadata(
        newFiles,
        assignedDocGroup,
        assignedDocType,
      );
      setFiles((prevFiles) => {
        if (isEmpty(prevFiles)) {
          return updatedFiles;
        }
        return [...prevFiles, ...updatedFiles];
      });
    },
    [assignedDocGroup, assignedDocType],
  );

  const handleUploadFiles = useCallback(
    (f) => {
      // console.log("Uploading files......", f);
      onSubmit(f);
    },
    [onSubmit],
  );

  // Apply initial docGroup/docType to initial files
  useEffect(() => {
    if (initialFiles && initialFiles.length > 0 && (docGroup || docType)) {
      const updatedFiles = updateFilesMetadata(initialFiles, docGroup, docType);
      setFiles(updatedFiles);
    }
  }, [initialFiles, docGroup, docType]);

  return (
    <Flx column sx={sx}>
      <FileUploadField
        key={`${keyName}-initial-upload`}
        id={`${keyName}-initial-file-upload`}
        suppressUploadButton
        borderSize={borderSize}
        background={background}
        docGroup={docGroup}
        docType={docType}
        hideFileList
        onFilesAdded={onFilesAdded}
      />
      <UploadedFilesTable files={files} setFiles={setFiles} />
      {isEmpty(files) ? null : (
        <Flx column g={2} sx={{ mt: 3 }}>
          <InputWrapper
            labelSx={{ fontStyle: "italic" }}
            label={
              disableCategoryAndTypeSelectionCount
                ? docGroupTypeLabel
                : `${docGroupTypeLabel} (${size(files)})`
            }
          >
            <SelectFileAssignmentInput
              placeholder="Select document type (e.g., Bank Statement, ID, Purchase Contract)"
              onChange={(params) =>
                onDocGroupTypeChange(params?.docGroup, params?.docType)
              }
              disabled={disableCategoryAndTypeSelection}
              docGroup={assignedDocGroup}
              docType={assignedDocType}
              fullWidth
              onlyDisplayDocType
            />
          </InputWrapper>
          <Flx
            g={1}
            fw
            ac
            end
            wrap
            sx={{
              mt: 4,
            }}
          >
            <Button
              onClick={() => setFiles(null)}
              variant="outlined"
              size="large"
              color="primary"
            >
              Clear All
            </Button>

            <Button
              loading={loading}
              onClick={() => handleUploadFiles(files)}
              size="large"
              color="primary"
            >
              Upload Files ({size(files)})
            </Button>
          </Flx>
        </Flx>
      )}
      {children}
    </Flx>
  );
};

const UploadedFilesTable = ({ files, setFiles }) => {
  const ref = useRef();

  const modalContentRef = useRef(null);

  const columnDefs = useMemo(
    () => [
      {
        resizable: false,
        cellRenderer: (params) => {
          return <AttachFileOutlined className="thin" sx={{ fontSize: 16 }} />;
        },
        // width: 24,
        maxWidth: 32,
        cellStyle: {
          paddingLeft: 0,
          paddingRight: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        },
      },
      {
        headerName: "Filename",
        field: "file_display_name",
        flex: 1,
        minWidth: 150,
        cellEditor: "agTextCellEditor",
        editable: true,
        cellStyle: {
          display: "flex",
          alignItems: "center",
          cursor: "text",
          gap: "6px",
          paddingLeft: "0px",
          // paddingLeft: "8px",
        },
      },

      {
        width: 38,
        headerName: "",
        pinned: "right",
        cellStyle: {
          paddingRight: 0,
          paddingLeft: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
        },
        field: "delete",
        cellRenderer: (params) => {
          return <CloseRounded className="thin" color="error" />;
        },
      },
    ],
    [],
  );

  const handleCellValueChanged = useCallback(
    (params) => {
      const { colDef, data, newValue } = params;

      // Create a copy of the data to update
      const updatedData = { ...data };

      // If the user changes the filename
      if (colDef.field === "file_display_name") {
        const originalExtension = data.name.split(".").pop().toLowerCase();
        const newValueWithoutExt = newValue.replace(/\.[^/.]+$/, "");
        updatedData.file_display_name = `${newValueWithoutExt}.${originalExtension}`;
      }

      // If the user changes docGroup
      if (colDef.field === "docGroup") {
        updatedData.docGroup = newValue;
        // Clear docType when docGroup changes
        updatedData.docType = "";
      }

      // If the user changes docType
      if (colDef.field === "docType") {
        updatedData.docType = newValue;
      }

      // Update the parent files state
      setFiles((prev) =>
        prev.map((item) => (item.id === data.id ? updatedData : item)),
      );

      setTimeout(() => {
        if (ref.current && ref.current.api) {
          ref.current.api.refreshCells();
        }
      }, 0);
    },
    [setFiles],
  );

  const onCellClicked = useCallback(
    (params) => {
      const { colDef, data } = params;

      if (colDef.field === "delete") {
        setFiles((prev) => prev.filter((item) => item.id !== data.id));
      }
    },
    [setFiles],
  );

  const columnTypes = useMemo(() => {
    return {
      ...columnTypesDate(),
      //   ...columnTypesNumber(),
    };
  }, []);

  if (isEmpty(files)) {
    return null;
  }

  return (
    <Box
      sx={{
        border: "1px solid #eeeeee",
        borderTop: "none",
        overflow: "hidden",
        borderBottomRightRadius: "8px",
        borderBottomLeftRadius: "8px",
        // mt: 1,
        ".ag-header": {
          border: "none !important",
        },
        ".ag-pinned-right-cols-container .ag-cell": {
          borderLeft: "1px solid #eeeeee !important",
        },

        ".ag-row-last": {
          borderBottom: "none !important",
        },
      }}
    >
      <AgGridTableWrapper simpleTable suppressMinHeightWhenPopulated={files}>
        <AgGridReact
          ref={ref}
          rowData={files}
          columnDefs={columnDefs}
          singleClickEdit
          onCellClicked={onCellClicked}
          columnTypes={columnTypes}
          domLayout={"autoHeight"}
          headerHeight={0}
          onCellValueChanged={handleCellValueChanged}
          stopEditingWhenCellsLoseFocus
          popupParent={modalContentRef.current}
        />
      </AgGridTableWrapper>
    </Box>
  );
};
export default FileUploadProcess;
